import { useState } from 'react'
import {
  useAssignableLessons, useStudentAssignments, usePublishedCourseVersions,
} from '@/hooks/useCurriculumAssignments'
import { useCurriculumAssets, type CurriculumAsset } from '@/hooks/useCurriculumAdmin'
import { SecureAssetViewer } from './SecureAssetViewer'
import { AssetThumbnail } from './AssetThumbnail'

interface CurriculumAssignmentPanelProps {
  studentId: string
  readOnly: boolean
}

export function CurriculumAssignmentPanel({ studentId, readOnly }: CurriculumAssignmentPanelProps) {
  const { lessons } = useAssignableLessons()
  const { versions } = usePublishedCourseVersions()
  const { assignments, saving, enrolled, enrollInCourse, assignLesson, markInCarComplete } = useStudentAssignments(studentId)
  const [selectedVersion, setSelectedVersion] = useState('')
  const [selectedLesson, setSelectedLesson] = useState('')
  const [error, setError] = useState('')
  const [preview, setPreview] = useState<{ assetId: string; assetType: 'pdf' | 'video'; title: string } | null>(null)

  const assignedLessonIds = new Set(assignments.map(a => a.lessonId))

  return (
    <div>
      {enrolled === false && !readOnly && (
        <div style={{ marginBottom: 16, padding: 14, background: 'rgba(212,151,43,0.08)', borderRadius: 10 }}>
          <div style={{ font: '500 13px Inter, sans-serif', color: '#9A6C0E', marginBottom: 8 }}>
            This student is not enrolled in a course yet — enroll them before assigning lessons.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select value={selectedVersion} onChange={e => setSelectedVersion(e.target.value)} className='field-input' style={{ flex: '1 1 220px' }}>
              <option value=''>Select a course…</option>
              {versions.map(v => <option key={v.id} value={v.id}>{v.courseTitle} — {v.title}</option>)}
            </select>
            <button className='btn-teal' style={{ width: 'auto', padding: '9px 16px' }} disabled={saving || !selectedVersion}
              onClick={async () => {
                const r = await enrollInCourse(selectedVersion)
                if (!r.ok) setError(r.error ?? 'Could not enroll student.')
              }}>
              Enroll
            </button>
          </div>
        </div>
      )}

      {assignments.length === 0 ? (
        <div style={{ color: 'rgba(27,42,74,0.4)', marginBottom: 14 }}>No lessons assigned yet.</div>
      ) : (
        assignments.map(a => (
          <AssignmentRow
            key={a.id}
            lessonId={a.lessonId}
            lessonTitle={a.lessonTitle}
            inCarCompletedAt={a.inCarCompletedAt}
            videoUnlockedAt={a.videoUnlockedAt}
            readOnly={readOnly}
            saving={saving}
            onMarkInCarComplete={async () => { const r = await markInCarComplete(a.id); if (!r.ok) setError(r.error ?? 'Could not update.') }}
            onPreview={setPreview}
          />
        ))
      )}

      {!readOnly && enrolled && (
        <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select value={selectedLesson} onChange={e => setSelectedLesson(e.target.value)} className='field-input' style={{ flex: '1 1 240px' }}>
            <option value=''>Select a lesson to assign…</option>
            {lessons.filter(l => !assignedLessonIds.has(l.lessonId)).map(l => (
              <option key={l.lessonId} value={l.lessonId}>{l.courseTitle} — {l.lessonTitle}</option>
            ))}
          </select>
          <button className='btn-teal' style={{ width: 'auto', padding: '9px 16px' }} disabled={saving || !selectedLesson}
            onClick={async () => {
              const r = await assignLesson(selectedLesson)
              if (r.ok) setSelectedLesson(''); else setError(r.error ?? 'Could not assign lesson.')
            }}>
            Assign lesson
          </button>
        </div>
      )}

      {error && <div className='banner-error' style={{ marginTop: 12 }}>{error}</div>}

      {preview && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(27,42,74,0.6)', zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
          }}
          onClick={() => setPreview(null)}
        >
          <div style={{ width: '100%', maxWidth: 820 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ font: '600 14px Inter, sans-serif', color: '#fff' }}>{preview.title}</div>
              <button onClick={() => setPreview(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>
            <SecureAssetViewer assetId={preview.assetId} assetType={preview.assetType} title={preview.title} />
          </div>
        </div>
      )}
    </div>
  )
}

// Fetches this lesson's own assets (own hook instance per row — each lesson's asset list
// is independent) so a "Preview" button can be offered per PDF/video that exists,
// regardless of this particular student's own unlock progress: the instructor is
// reviewing the COURSE CONTENT itself, not standing in for the student's own gated view.
// Relies on the already-existing "curriculum_lesson_assets: teaching role read" policy
// (migration 038) — instructor/school_admin already have full read access to every asset
// of a published lesson, so this needed no new RLS.
function AssignmentRow({
  lessonId, lessonTitle, inCarCompletedAt, videoUnlockedAt, readOnly, saving, onMarkInCarComplete, onPreview,
}: {
  lessonId: string
  lessonTitle: string
  inCarCompletedAt: string | null
  videoUnlockedAt: string | null
  readOnly: boolean
  saving: boolean
  onMarkInCarComplete: () => void
  onPreview: (p: { assetId: string; assetType: 'pdf' | 'video'; title: string }) => void
}) {
  const { assets } = useCurriculumAssets(lessonId)

  const openPreview = (asset: CurriculumAsset) => {
    if (asset.assetType === 'text') return
    onPreview({ assetId: asset.id, assetType: asset.assetType, title: asset.title })
  }

  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid rgba(27,42,74,0.05)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px', font: '500 13.5px Inter, sans-serif', color: '#1B2A4A' }}>{lessonTitle}</div>
        <div style={{
          padding: '3px 10px', borderRadius: 20,
          background: videoUnlockedAt ? 'rgba(15,124,108,0.1)' : inCarCompletedAt ? 'rgba(27,42,74,0.08)' : 'rgba(212,151,43,0.12)',
          color: videoUnlockedAt ? '#0F7C6C' : inCarCompletedAt ? '#1B2A4A' : '#9A6C0E',
          font: '600 11px Inter, sans-serif',
        }}>
          {videoUnlockedAt ? 'Video unlocked' : inCarCompletedAt ? 'In-car done' : 'PDF assigned'}
        </div>
        {assets.filter(a => a.assetType !== 'text').map(a => (
          <AssetThumbnail
            key={a.id} assetId={a.id} assetType={a.assetType as 'pdf' | 'video'} title={a.title}
            onClick={() => openPreview(a)} width={100} height={64}
          />
        ))}
        {!readOnly && !inCarCompletedAt && (
          <button
            onClick={onMarkInCarComplete}
            disabled={saving}
            style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid rgba(15,124,108,0.3)', background: 'transparent', font: '500 12px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer' }}
          >
            Mark in-car complete → unlock video
          </button>
        )}
      </div>
    </div>
  )
}
